import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { MAX_CACHE_MS } from './api/client'
import { isRetryable, isUnexpectedError } from './api/errors'
import { Layout } from './components/Layout'
import { NotFoundPage } from './pages/NotFoundPage'
import { RecordDetailPage } from './pages/RecordDetailPage'
import { SearchPage } from './pages/SearchPage'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Only transport and 5xx failures are retried. A 429 in particular must never
      // be retried automatically — that is what the rate limit is protecting against.
      retry: (failureCount, error) => isRetryable(error) && failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      // Background refetching would spend the 60 req/min budget on data the API
      // itself caches for five minutes.
      refetchOnWindowFocus: false,
      // Never hold a response longer than the API's own max-age: corrections
      // overwrite records in place, so a stale copy can be a superseded legal
      // record shown as current.
      staleTime: MAX_CACHE_MS,
      gcTime: MAX_CACHE_MS,
      // Server and transport faults are the archive's own problem, so they go to
      // the error boundary with a support address. A 404, 429, page cap or
      // validation message stays inline where the reader can act on it.
      throwOnError: isUnexpectedError,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Navigate to="/search" replace />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/records/:id" element={<RecordDetailPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
